// LSP-16086: a pure coalescing scheduler for socket-driven (and periodic) timeline
// reloads. It collapses a burst of reload requests into a single flush so the
// contact-api `/search/media` endpoint is not hammered (see the crowd-test incident),
// while a MAX_WAIT ceiling guarantees a flush during a continuous stream so viewers
// still see new media. Kept pure (no Vue / DOM) so the debounce + ceiling + settle
// behaviour is unit-testable with fake timers.

export type ReloadMode = "delta" | "full";

export interface ReloadSurfaces {
	timeline: boolean;
	documents: boolean;
	checklists: boolean;
	checklistMedia: boolean;
}

export type PartialReloadSurfaces = Partial<ReloadSurfaces>;

export interface TimelineReloadSchedulerOptions {
	// Trailing-debounce window. Each schedule() resets it so socket bursts produce
	// one PostgreSQL refresh instead of one query per event.
	wait: number;
	// Ceiling from the first event of a burst — flush even if events keep arriving,
	// so a continuous upload stream still refreshes within a bounded time.
	maxWait: number;
	// Invoked once per coalesced burst with the merged surfaces and escalated mode.
	flush: (surfaces: ReloadSurfaces, mode: ReloadMode) => void;
}

export interface TimelineReloadScheduler {
	schedule: (surfaces: PartialReloadSurfaces, mode: ReloadMode) => void;
	cancel: () => void;
	isPending: () => boolean;
}

export function createTimelineReloadScheduler(options: TimelineReloadSchedulerOptions): TimelineReloadScheduler {
	let debounceTimer: ReturnType<typeof setTimeout> | null = null;
	let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
	const flags: ReloadSurfaces = { timeline: false, documents: false, checklists: false, checklistMedia: false };
	let mode: ReloadMode = "delta";

	function clearTimers() {
		if (debounceTimer) {
			clearTimeout(debounceTimer);
			debounceTimer = null;
		}
		if (maxWaitTimer) {
			clearTimeout(maxWaitTimer);
			maxWaitTimer = null;
		}
	}

	function reset() {
		flags.timeline = false;
		flags.documents = false;
		flags.checklists = false;
		flags.checklistMedia = false;
		mode = "delta";
	}

	function doFlush() {
		// Capture-and-clear atomically BEFORE invoking flush, so a schedule() raised
		// during the (possibly async) flush callback begins a fresh burst — its flags
		// re-arm the timers — rather than being silently wiped. This is what prevents
		// the last event of a stream from being stranded after a MAX_WAIT flush.
		const captured: ReloadSurfaces = { ...flags };
		const capturedMode = mode;
		clearTimers();
		reset();
		options.flush(captured, capturedMode);
	}

	function schedule(surfaces: PartialReloadSurfaces, requestedMode: ReloadMode) {
		if (surfaces.timeline) flags.timeline = true;
		if (surfaces.documents) flags.documents = true;
		if (surfaces.checklists) flags.checklists = true;
		if (surfaces.checklistMedia) flags.checklistMedia = true;
		// Monotonic escalation: full wins and never downgrades within a burst (a
		// recording update needs a full reload; a later snapshot delta must not undo it).
		if (requestedMode === "full") mode = "full";

		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = setTimeout(doFlush, options.wait);

		// Arm the ceiling once per burst; doFlush clears it, the next schedule re-arms.
		if (!maxWaitTimer) {
			maxWaitTimer = setTimeout(doFlush, options.maxWait);
		}
	}

	function cancel() {
		clearTimers();
		reset();
	}

	function isPending() {
		return debounceTimer !== null || maxWaitTimer !== null;
	}

	return { schedule, cancel, isPending };
}
